import React, { useState, useEffect } from "react";
import { HardDrive, RefreshCw, AlertCircle, Cloud, FileText, Database, ShieldCheck, Info } from "lucide-react";
import { fetchStorageTelemetry, StorageTelemetryResponse } from "./settingsApi";

export const StorageManagementPage: React.FC = () => {
  const [telemetry, setTelemetry] = useState<StorageTelemetryResponse | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [refreshing, setRefreshing] = useState<boolean>(false);
  const [error, setError] = useState<string>("");

  useEffect(() => {
    loadTelemetry(true);
  }, []);

  const loadTelemetry = async (isInitial = false) => {
    if (isInitial) setLoading(true);
    else setRefreshing(true);
    setError("");

    try {
      const data = await fetchStorageTelemetry();
      setTelemetry(data);
    } catch (err: any) {
      setError(err?.response?.data?.detail || "Failed to load storage telemetry.");
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-12 text-slate-400 text-xs">
        <RefreshCw className="w-5 h-5 animate-spin mr-2 text-amber-400" />
        <span>Inspecting storage backends and R2 bucket status...</span>
      </div>
    );
  }

  const provider = telemetry?.provider;
  const metrics = telemetry?.metrics;

  return (
    <div className="space-y-4 text-xs font-sans">
      {error && (
        <div className="flex items-center gap-2 p-3 bg-red-950/60 border border-red-800 text-red-300 rounded-lg text-xs">
          <AlertCircle className="w-4 h-4 shrink-0" />
          <span>{error}</span>
        </div>
      )}

      {/* Header */}
      <div className="flex items-center justify-between p-4 bg-slate-900/80 border border-slate-800 rounded-xl">
        <div className="flex items-center gap-2">
          <HardDrive className="w-5 h-5 text-amber-400" />
          <div>
            <div className="flex items-center gap-2">
              <h2 className="text-sm font-bold text-slate-100">Storage Management & R2 Telemetry</h2>
              <span className={`px-2 py-0.5 rounded text-[10px] font-mono font-bold ${
                provider?.r2_connected
                  ? "bg-emerald-950 text-emerald-400 border border-emerald-800"
                  : provider?.r2_configured
                  ? "bg-amber-950 text-amber-400 border border-amber-800"
                  : "bg-slate-800 text-slate-400 border border-slate-700"
              }`}>
                {provider?.status_text}
              </span>
            </div>
            <p className="text-[10px] text-slate-500">
              Active Tenant Schema: <code className="text-amber-400 font-mono">{telemetry?.tenant_schema || "public"}</code> · Isolated Object Path Prefix
            </p>
          </div>
        </div>

        <button
          onClick={() => loadTelemetry(false)}
          disabled={refreshing}
          className="flex items-center gap-1.5 px-3 py-1.5 bg-slate-800 hover:bg-slate-700 disabled:opacity-50 text-slate-200 rounded-lg transition-colors text-[11px]"
        >
          <RefreshCw className={`w-3.5 h-3.5 ${refreshing ? "animate-spin text-amber-400" : ""}`} />
          <span>{refreshing ? "Refreshing..." : "Refresh"}</span>
        </button>
      </div>

      {/* Provider Details Bar */}
      <div className="p-4 bg-slate-950 border border-slate-800 rounded-xl space-y-2">
        <h3 className="text-xs font-bold text-slate-200 flex items-center gap-2 font-sans">
          <Cloud className="w-4 h-4 text-indigo-400" />
          Primary Storage Provider Infrastructure
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 font-mono text-[11px]">
          <div className="p-2.5 bg-slate-900/90 border border-slate-800 rounded-lg">
            <span className="text-slate-500 block text-[10px] font-sans">Storage Backend Engine</span>
            <span className="font-bold text-slate-200 capitalize">{provider?.backend_type || "local"}</span>
          </div>

          <div className="p-2.5 bg-slate-900/90 border border-slate-800 rounded-lg">
            <span className="text-slate-500 block text-[10px] font-sans">R2 Offsite Bucket Name</span>
            <span className="font-bold text-amber-400 truncate block">{provider?.r2_bucket_name}</span>
          </div>

          <div className="p-2.5 bg-slate-900/90 border border-slate-800 rounded-lg">
            <span className="text-slate-500 block text-[10px] font-sans">Public Asset Domain</span>
            <span className="font-bold text-indigo-300 truncate block">{provider?.public_url}</span>
          </div>
        </div>

        {provider?.error_detail && (
          <div className="p-2.5 bg-amber-950/40 border border-amber-900/60 rounded text-amber-300 text-[10px] flex items-center gap-2">
            <AlertCircle className="w-4 h-4 shrink-0 text-amber-400" />
            <span>{provider.error_detail}</span>
          </div>
        )}
      </div>

      {/* Real Storage Categories */}
      <div className="space-y-3 font-mono">
        {/* Tenant Media */}
        <div className="p-4 bg-slate-950 border border-slate-800 rounded-xl space-y-2">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <span className="text-[9px] font-bold px-1.5 py-0.5 rounded border uppercase bg-blue-950 text-blue-300 border-blue-800">
                Media & Uploads
              </span>
              <span className="text-xs font-bold text-slate-100 font-sans">Tenant Media Directory</span>
            </div>
            <span className="text-xs font-bold text-blue-400">
              {metrics?.tenant_media.formatted_size} ({metrics?.tenant_media.file_count} files)
            </span>
          </div>
          <div className="flex items-center gap-2 text-[10px] text-slate-500 font-sans">
            <span>Storage Path Prefix:</span>
            <code className="text-slate-300 bg-slate-900 px-1.5 py-0.5 rounded border border-slate-800 font-mono">
              {metrics?.tenant_media.path_prefix}
            </code>
          </div>
        </div>

        {/* Local Database Backups */}
        <div className="p-4 bg-slate-950 border border-slate-800 rounded-xl space-y-2">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <span className="text-[9px] font-bold px-1.5 py-0.5 rounded border uppercase bg-emerald-950 text-emerald-300 border-emerald-800">
                Local Backups
              </span>
              <span className="text-xs font-bold text-slate-100 font-sans">Local Dump Storage</span>
            </div>
            <span className="text-xs font-bold text-emerald-400">
              {metrics?.local_backups.formatted_size} ({metrics?.local_backups.file_count} archives)
            </span>
          </div>
          <div className="flex items-center gap-2 text-[10px] text-slate-500 font-sans">
            <span>Server File Path:</span>
            <code className="text-slate-300 bg-slate-900 px-1.5 py-0.5 rounded border border-slate-800 font-mono">
              {metrics?.local_backups.path}
            </code>
          </div>
        </div>

        {/* Cloudflare R2 Offsite Backups Audit */}
        <div className="p-4 bg-slate-950 border border-slate-800 rounded-xl space-y-2">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <span className="text-[9px] font-bold px-1.5 py-0.5 rounded border uppercase bg-purple-950 text-purple-300 border-purple-800">
                R2 Offsite Archives
              </span>
              <span className="text-xs font-bold text-slate-100 font-sans">Cloudflare R2 Database Snapshots</span>
            </div>
            <span className="text-xs font-bold text-purple-400">
              {metrics?.backup_jobs_audit.offsite_r2_count} R2 Backups Recorded
            </span>
          </div>
          <div className="flex items-center gap-2 text-[10px] text-slate-500 font-sans">
            <span>Latest Snapshot Completed:</span>
            <span className="text-slate-300 font-mono">
              {metrics?.backup_jobs_audit.latest_completed
                ? new Date(metrics.backup_jobs_audit.latest_completed).toLocaleString()
                : "None Recorded"}
            </span>
          </div>
        </div>
      </div>

      {/* Unsupported Metric Callout */}
      <div className="p-3 bg-slate-950 border border-slate-800 rounded-xl flex items-start gap-2 text-[11px] text-slate-400">
        <Info className="w-4 h-4 text-slate-500 shrink-0 mt-0.5" />
        <div>
          <span className="font-bold text-slate-300 block mb-0.5">Capacity Metric Limitation</span>
          <p className="text-[10px] text-slate-500">
            Total Cloud Storage Disk Capacity: <strong className="text-slate-400 font-mono">Unavailable</strong> (Cloudflare R2 provides auto-scaling object storage without artificial fixed disk quota limits).
          </p>
        </div>
      </div>
    </div>
  );
};
